<template>
    <div class="container-fluid default-dashboard">
        <div class="card">
            <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                    <h5>Bank List</h5>
                    <button class="btn btn-primary" @click="openModal()">
                        <i class="fa fa-plus"></i> Add Bank
                    </button>
                </div>
            </div>
            <div class="card-body">
                <DataTable
                    :headers="headers"
                    :items="banks"
                    :loading="loading"
                    :total="total"
                    :params="params"
                    @update:params="updateParams"
                >
                    <template #item.actions="{ item }">
                        <button class="btn btn-sm btn-info me-2" @click="viewBank(item as Bank)">
                            <i class="fa fa-eye"></i>
                        </button>
                        <button class="btn btn-sm btn-warning me-2" @click="editBank(item as Bank)">
                            <i class="fa fa-edit"></i>
                        </button>
                        <button class="btn btn-sm btn-danger" @click="deleteBank(item as Bank)">
                            <i class="fa fa-trash"></i>
                        </button>
                    </template>
                </DataTable>
            </div>
        </div>
    </div>

    <div class="modal fade bd-example-modal-lg" id="bankModal" tabindex="-1" ref="modalRef" aria-labelledby="bankModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg" role="document">
            <div class="modal-content">
                <h3 class="modal-header justify-content-center border-0">{{ modalTitle }}</h3>
                    <div class="modal-body">
                        <div class="row">
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Code</label>
                                <input v-model="form.code" type="text" class="form-control" required>
                            </div>
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Name</label>
                                <input v-model="form.name" type="text" class="form-control" required>
                            </div>
                        </div>
                        <hr>
                        <div class="mb-2">
                            <label class="form-label col-form-label">Accounts</label>
                            <div v-for="(account, idx) in form.accounts" :key="account.ID ?? idx" class="border rounded p-2 mb-2">
                                <div class="row g-2">
                                    <div class="col-md-4">
                                        <input v-model="account.accountNumber" type="text" class="form-control" placeholder="Account Number" required>
                                    </div>
                                    <div class="col-md-4">
                                        <input v-model="account.accountName" type="text" class="form-control" placeholder="Account Name" required>
                                    </div>
                                    <div class="col-md-3">
                                        <input v-model="account.branch" type="text" class="form-control" placeholder="Cabang" required>
                                    </div>
                                    <div class="col-md-1 d-flex align-items-center">
                                        <button type="button" class="btn btn-danger btn-sm" @click="removeAccount(idx)">
                                            <i class="fa fa-trash"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button type="button" class="btn btn-success btn-sm mt-2" @click="addAccount">
                            <i class="fa fa-plus"></i> Add Account
                        </button>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-danger" type="button" @click="closeModal">Cancel</button>
                        <button class="btn btn-primary" type="submit" @click="saveBank">Save</button>
                    </div>
            </div>
        </div>
    </div>

    <div class="modal fade" id="viewBankModal" tabindex="-1" ref="viewModalRef">
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Bank & Account Information</h5>
                    <button type="button" class="btn-close" @click="closeViewModal"></button>
                </div>
                <div class="modal-body">
                    <div v-if="selectedBank">
                        <div class="mb-2"><strong>Code:</strong> {{ selectedBank.code }}</div>
                        <div class="mb-2"><strong>Bank:</strong> {{ selectedBank.name }}</div>
                        <div>
                            <strong>Accounts:</strong>
                            <ul v-if="selectedBank.accounts && selectedBank.accounts.length">
                                <li v-for="(account, idx) in selectedBank.accounts" :key="account.ID ?? idx">
                                    <span>
                                        <strong>No:</strong> {{ account.accountNumber }},
                                        <strong>Nama:</strong> {{ account.accountName }},
                                        <strong>Cabang:</strong> {{ account.branch }}
                                    </span>
                                </li>
                            </ul>
                            <div v-else>
                                <em>No accounts available.</em>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="closeViewModal">Close</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import DataTable from '@/components/table/Datatable.vue'
import { Modal } from 'bootstrap'

const banks = ref<Bank[]>([])
const loading = ref(false)
const total = ref(0)
const modalTitle = ref('')
const params = reactive<DataTableParams>({
    page: 1,
    size: 10,
    search: ''
})

const headers = [
    { text: 'Code', value: 'code' },
    { text: 'Name', value: 'name' },
    { text: 'Actions', value: 'actions' }
]

const initForm = {
    ID: null,
    code: '',
    name: '',
    accounts: [] as {
        ID: number | null,
        accountNumber: string,
        accountName: string,
        branch: string
    }[]
}

const form = reactive({
    ...initForm
})

const dataBanks: Bank[] = []
import * as master from "@/api/services/MasterRequest";

const fetchBanks = async () => {
    loading.value = true
    try {
        const resp = await master.postRequest("/bank/list", {...params});
        dataBanks.splice(0, dataBanks.length);
        dataBanks.push(...resp.data);

        let filteredBanks = [...dataBanks]
        if (params.search) {
            const searchLower = params.search.toLowerCase()
            filteredBanks = dataBanks.filter(bank =>
                bank.code.toLowerCase().includes(searchLower) ||
                bank.name.toLowerCase().includes(searchLower)
            )
        }

        const start = (params.page - 1) * params.size
        const end = start + params.size
        banks.value = filteredBanks.slice(start, end)
        total.value = filteredBanks.length

    } catch (error) {
        toast.error('Failed to fetch banks')
    }
    loading.value = false
}

const updateParams = (newParams: Partial<DataTableParams>) => {
    Object.assign(params, newParams)
    fetchBanks()
}

const modalRef = ref<HTMLElement | null>(null)
let bsModal: any | null = null

const viewModalRef = ref<HTMLElement | null>(null)
let bsViewModal: any | null = null
const selectedBank = ref<Bank | null>(null)

onMounted(() => {
    if (modalRef.value) {
        bsModal = new Modal(modalRef.value)
    }
    if (viewModalRef.value) {
        bsViewModal = new Modal(viewModalRef.value)
    }
})

const closeModal = () => {
    if (bsModal) {
        bsModal.hide()
    }
}

const editBank = (bank: Bank) => {
    openModal(bank);
}

const saveBank = async () => {
    loading.value = true
    try {
        if (form.ID) {
            await master.postRequest("/bank/update", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Bank updated successfully');
                    closeModal();
                    fetchBanks();
                } else {
                    toast.error('Failed to update bank');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to update bank');
            })
        } else {
            await master.postRequest("/bank/create", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Bank created successfully');
                    closeModal();
                    fetchBanks();
                } else {
                    toast.error('Failed to create bank');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to create bank');
            })
        }
    } catch (error: any) {
        toast.error(error.message || 'Failed to save bank');
    } finally {
        loading.value = false
    }
}

const deleteBank = async (bank: Bank) => {
    if (!confirm('Are you sure you want to delete this bank?')) return

    loading.value = true
    try {
        await master.postRequest("/bank/delete", {ID: bank.ID}).then((resp) => {
            if (resp.status === 200) {
                toast.success('Bank deleted successfully');
                closeModal();
                fetchBanks();
            } else {
                toast.error('Failed to delete bank');
            }
        })

    } catch (error: any) {
        toast.error(error.message || 'Failed to delete bank');
    } finally {
        loading.value = false
    }
}

const addAccount = () => {
    form.accounts = form.accounts || [];
    form.accounts.push({
        ID: null,
        accountNumber: '',
        accountName: '',
        branch: ''
    });
};

const removeAccount = (idx: number) => {
    if (form.accounts) form.accounts.splice(idx, 1);
};

const openModal = (bank: Bank | null = null) => {
    if (bank) {
        modalTitle.value = 'Edit Bank'
        Object.assign(form, bank);
        form.accounts = bank.accounts ? bank.accounts.map(acc => ({ ...acc })) : [];
    } else {
        modalTitle.value = 'Create Bank'
        Object.assign(form, {...initForm})
        console.log(form)
        console.log(initForm)
    }
    if (bsModal) {
        bsModal.show()
    }
}

const viewBank = (bank: Bank) => {
    selectedBank.value = bank
    if (!bsViewModal && viewModalRef.value) {
        bsViewModal = new Modal(viewModalRef.value)
    }
    if (bsViewModal) {
        bsViewModal.show()
    }
}

const closeViewModal = () => {
    if (bsViewModal) {
        bsViewModal.hide()
    }
    selectedBank.value = null
}
</script>